import React from 'react';
import { Text, IconAudioLine, Button, Popover, View, IconAudioOffLine, IconButton, RangeInput, IconEmptySolid } from '@instructure/ui';
import { capitalizeFirstLetter } from '@instructure/ui-utils';
import { randomId } from '../../../helpers/utilities';
import { useCallback, useEffect, useState, useRef } from 'react';
import { getStore, usePlayerStore } from '../../../stores/StoreContext';
import { NavigationalToolTip } from '@ui/shared/components/Common/NavigationalToolTip';
import { useFullScreenMountNode } from '../../../contexts/FullScreenContext';
import CONSTANTS from '../../../config/constants';

/**
 * Volume Control Component
 * 
 * A comprehensive volume control interface for the video player featuring:
 * - Click-to-open popover with vertical volume slider (90px height)
 * - Smooth drag-to-adjust volume functionality with real-time feedback
 * - Visual volume fill indicator that grows from bottom up (0% = bottom, 100% = top)
 * - Mute/unmute toggle via double-click on volume icon
 * - Keyboard shortcut integration (M key for mute toggle)
 * - Real-time audio volume control with immediate video element updates
 * - Accessibility support with proper ARIA labels and screen reader text
 * 
 * @component
 * 
 * @example
 * // Basic usage in player controls
 * import Volume from './Volume';
 * 
 * function PlayerControls() {
 *   return (
 *     <div className="player-controls">
 *       <Volume />
 *     </div>
 *   );
 * }
 * 
 * @example
 * // The component integrates with PlayerStore for state management:
 * // - PlayerStore.setVolume(0-100) - Sets volume percentage
 * // - PlayerStore.setMuted(boolean) - Sets mute state
 * // - PlayerStore.hlsVideoRef - Reference to video element for direct control
 * 
 * @example
 * // Keyboard shortcuts (handled in usePlayerHotkeys):
 * // - M key: Toggle mute/unmute
 * // - Double-click volume icon: Toggle mute/unmute
 * 
 * @example
 * // CSS classes used (defined in styles.css):
 * // - .volume-fill: Forces bottom positioning for volume indicator
 * // - .volume-handle: Styles the draggable handle (white circle with border)
 * 
 * @requires PlayerStore - Zustand store for volume state management
 * @requires InstructureUI - UI components (View, IconButton, Popover)
 * @requires styles.css - CSS classes for volume-fill and volume-handle
 * 
 * @returns {JSX.Element} Volume control with popover slider interface
 */
const Volume = () => {
  // Store access for volume control actions
  const PlayerStore = getStore('playerStore');

  // Get the appropriate mount node for full screen support
  const mountNode = useFullScreenMountNode();

  // UI Configuration Constants
  /** @const {number} Height of the volume slider in pixels */
  const SLIDER_HEIGHT = 90;
  /** @const {number} Total height of the popover container in pixels */
  const POPOVER_HEIGHT = 115;

  // Store State Subscriptions
  /** @type {number} Current volume level (0-100) */
  const volume = usePlayerStore((state) => state.volume);
  /** @type {boolean} Current mute state */
  const muted = usePlayerStore((state) => state.muted);

  // Component State
  /** @type {React.RefObject<HTMLDivElement>} Reference to slider container for mouse calculations */
  const sliderRef = useRef(null);
  /** @type {React.RefObject<HTMLDivElement>} Reference to volume handle for focus management */
  const handleRef = useRef(null);
  /** @type {boolean} Tracks if user is currently dragging the volume handle */
  const [isDragging, setIsDragging] = useState(false);
  /** @type {boolean} Tracks if the popover is currently open */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  /**
   * Updates volume based on mouse Y position within the slider
   * 
   * Calculates volume percentage from mouse position:
   * - Top of slider = 100% volume
   * - Bottom of slider = 0% volume
   * - Automatically updates PlayerStore with new volume
   * 
   * @param {number} clientY - Mouse Y coordinate from MouseEvent.clientY
   */
  const updateVolumeFromPosition = useCallback((clientY) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clickY = clientY - rect.top;
    const sliderHeight = rect.height;

    // Calculate percentage (0% at bottom, 100% at top)
    // Inverted calculation: top click = high volume, bottom click = low volume
    const percentage = Math.max(0, Math.min(100, 100 - (clickY / sliderHeight) * 100));

    PlayerStore.setVolume(Math.round(percentage));
  }, [PlayerStore]);

  /**
   * Handles direct clicks on the slider track
   * 
   * @param {MouseEvent} event - Mouse click event
   */
  const handleSliderClick = useCallback((event) => {
    updateVolumeFromPosition(event.clientY);
  }, [updateVolumeFromPosition]);

  /**
   * Initiates drag operation when mouse is pressed on slider or handle
   * 
   * Prevents default browser behavior and starts drag tracking.
   * Immediately updates volume to clicked position.
   * 
   * @param {MouseEvent} event - Mouse down event
   */
  const handleMouseDown = useCallback((event) => {
    event.preventDefault();    // Prevent text selection
    event.stopPropagation();   // Prevent event bubbling
    setIsDragging(true);       // Enable drag mode
    updateVolumeFromPosition(event.clientY);  // Set initial position
  }, [updateVolumeFromPosition]);

  /**
   * Handles keyboard input for volume control
   * 
   * Supports arrow keys for volume adjustment:
   * - Up Arrow / Right Arrow: Increase volume by 5%
   * - Down Arrow / Left Arrow: Decrease volume by 5%
   * - Page Up: Increase volume by 10%
   * - Page Down: Decrease volume by 10%
   * - Home: Set volume to 100%
   * - End: Set volume to 0%
   * 
   * @param {KeyboardEvent} event - Keyboard event
   */
  const handleKeyDown = useCallback((event) => {
    let newVolume = volume;

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        event.preventDefault();
        newVolume = Math.min(100, volume + 5);
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        event.preventDefault();
        newVolume = Math.max(0, volume - 5);
        break;
      case 'PageUp':
        event.preventDefault();
        newVolume = Math.min(100, volume + 10);
        break;
      case 'PageDown':
        event.preventDefault();
        newVolume = Math.max(0, volume - 10);
        break;
      case 'Home':
        event.preventDefault();
        newVolume = 100;
        break;
      case 'End':
        event.preventDefault();
        newVolume = 0;
        break;
      default:
        return; // Don't update volume for other keys
    }

    PlayerStore.setVolume(newVolume);
  }, [volume, PlayerStore]);

  /**
   * Global drag event handling effect
   * 
   * Manages document-level mouse events during drag operations:
   * - Attaches global mousemove/mouseup listeners when dragging starts
   * - Ensures drag continues even if mouse leaves slider area
   * - Cleans up event listeners when dragging stops
   * - Uses { passive: false } to allow preventDefault() calls
   */
  useEffect(() => {
    /**
     * Handles mouse movement during drag operations
     * @param {MouseEvent} event - Mouse move event
     */
    const handleMouseMove = (event) => {
      if (!isDragging) return;
      event.preventDefault();  // Prevent scrolling/selection during drag
      updateVolumeFromPosition(event.clientY);
    };

    /**
     * Handles mouse release to end drag operation
     */
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // Only attach global listeners during active drag
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp);

      // Cleanup function removes listeners when effect re-runs or component unmounts
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, updateVolumeFromPosition]);

  /**
   * Focus management effect for popover
   * 
   * When the popover opens, automatically focus the volume handle
   * for immediate keyboard interaction and accessibility
   */
  useEffect(() => {
    if (isPopoverOpen && handleRef.current) {
      // Small delay to ensure popover is fully rendered
      setTimeout(() => {
        handleRef.current?.focus();
      }, 100);
    }
  }, [isPopoverOpen]);

  return (
    <View>
      {/* Main Volume Control Popover */}
      <Popover
        renderTrigger={
          /* Volume Icon Button - Shows current volume/mute state */
          <IconButton
            screenReaderLabel="Adjust playback volume"
            size="small"
            color={muted ? 'danger' : 'secondary'}
            onDoubleClick={() => {
              // Double-click toggles mute state
              PlayerStore.setMuted(!muted);
            }}
          >
            {/* Dynamic icon based on mute state and volume level */}
            {muted ? <IconAudioOffLine /> : <IconAudioLine />}
          </IconButton>
        }
        on="click"                          // Opens on single click
        screenReaderLabel="Volume control"
        shouldContainFocus={false}          // Allows interaction outside popover
        shouldReturnFocus={false}           // Doesn't force focus back to trigger
        shouldCloseOnDocumentClick={true}   // Closes when clicking elsewhere
        offsetY="0px"
        placement="bottom"                  // Appears below the icon
        mountNode={() => mountNode}         // Mount to full screen container when in full screen mode
        onToggle={(isOpen) => setIsPopoverOpen(isOpen)}  // Track popover state
      >
        {/* Popover Content Container */}
        <View as="div" padding="small" display="block" width="40px" height={`${POPOVER_HEIGHT}px`}>
          {/* Interactive Slider Container - Native div for proper ref handling */}
          <div
            ref={sliderRef}                    // Required for mouse position calculations
            onMouseDown={handleMouseDown}      // Handles clicks anywhere on slider
            style={{
              position: 'relative',
              width: '100%',
              height: `${SLIDER_HEIGHT}px`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {/* Outer Track Border - Provides visual boundary */}
            <View
              as="div"
              position="absolute"
              width="10px"
              height={`${SLIDER_HEIGHT + 2}px`}   // Slightly taller for border effect
              background="secondary"            // Background color
              borderWidth="small"               // Border thickness
              borderColor="primary"             // Border color
              borderRadius="8px"
              style={{
                left: '50%',
                transform: 'translateX(-50%)'   // Center horizontally
              }}
            />

            {/* Inner Track Background - Empty slider area */}
            <View
              as="div"
              position="absolute"
              width="8px"
              height={`${SLIDER_HEIGHT}px`}
              borderRadius="medium"
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                top: '1px'                      // Offset for border
              }}
            />

            {/* Volume Fill Indicator - Shows current volume level */}
            <View
              as="div"
              position="absolute"
              focusWithin={true}
              className="volume-fill"           // CSS class for bottom positioning
              width="8px"
              background="brand"                // Brand color for volume level
              borderRadius="8px"
              height={`${volume * (SLIDER_HEIGHT / 100)}px`}  // Dynamic height based on volume
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                // Note: bottom: 0px is set via CSS class due to InstructureUI conflicts
              }}
            />

            {/* Volume Handle - Draggable control element */}
            <View
              as="div"
              position="absolute"
              className="volume-handle"         // CSS class for styling (white circle with border)
              onMouseDown={handleMouseDown}     // Initiates drag operation
              onKeyDown={handleKeyDown}         // Handles keyboard volume control
              tabIndex={0}                      // Make focusable for keyboard interaction
              role="slider"                     // Accessibility role
              aria-label="Volume level"        // Screen reader label
              aria-valuemin={0}                 // Minimum volume value
              aria-valuemax={100}               // Maximum volume value
              aria-valuenow={volume}            // Current volume value
              elementRef={(el) => {
                handleRef.current = el;
              }}            // Reference for focus management
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                // Position from top: total height - (volume percentage * height) - handle offset
                top: `${SLIDER_HEIGHT - (volume * (SLIDER_HEIGHT / 100)) - 8}px`,
                cursor: isDragging ? 'grabbing' : 'grab',  // Visual feedback for drag state
                zIndex: 10,                     // Ensure handle appears above other elements
                outline: 'none'                 // Remove default focus outline (styled via CSS)
              }}
            >
              {/* Handle is styled via CSS - no icon content needed */}
            </View>
          </div>

        </View>
      </Popover>
    </View>
  );
};

export default Volume;
